import React, { useState, useRef, useEffect } from 'react';
import { cn, getImageUrl } from '../lib/utils';
import articlePlaceholder from '../assets/article-placeholder.svg';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  wrapperClassName?: string;
  /** When true the image is in the viewport and should load eagerly (above-the-fold) */
  priority?: boolean;
}

/**
 * Optimised lazy image with:
 * - Instant local SVG placeholder (no network round-trip)
 * - Shimmer skeleton until the real image loads
 * - Smooth blur-up reveal once loaded
 * - Graceful fallback to local SVG on broken URL
 * - decoding="async" + fetchpriority support
 */
export function LazyImage({
  src,
  alt,
  className,
  wrapperClassName,
  priority = false,
  ...props
}: LazyImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Resolve the real URL once
  const resolvedSrc = getImageUrl(src);

  // If the image is already cached by the browser it fires `load` before React
  // attaches the handler – detect that case and mark as loaded immediately.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [resolvedSrc]);

  // Decide whether the src looks like a real uploaded image URL or an
  // external AI-generated / broken URL we should skip right away.
  const isMissing = !src || src.trim() === '';

  return (
    <div className={cn('relative overflow-hidden bg-zinc-100 dark:bg-zinc-800/50', wrapperClassName)}>
      {/* ── Shimmer skeleton shown while the real image is loading ── */}
      {!isLoaded && !hasError && !isMissing && (
        <div
          className="absolute inset-0 bg-gradient-to-r from-zinc-200 via-zinc-100 to-zinc-200 dark:from-zinc-800 dark:via-zinc-700 dark:to-zinc-800 animate-[shimmer_1.4s_ease-in-out_infinite] bg-[length:200%_100%]"
          aria-hidden="true"
        />
      )}

      {/* ── Fallback: missing src OR load error → local SVG placeholder ── */}
      {(hasError || isMissing) ? (
        <img
          src={articlePlaceholder}
          alt={alt || 'Article image'}
          className={cn('w-full h-full object-cover opacity-90', className)}
          aria-hidden={!alt}
          loading="lazy"
        />
      ) : (
        <img
          ref={imgRef}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          // fetchpriority is a valid HTML attribute but not yet in React's types
          {...({ fetchpriority: priority ? 'high' : 'low' } as any)}
          src={resolvedSrc}
          alt={alt}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
          className={cn(
            'transition-[opacity,filter,transform] duration-700 ease-out will-change-[opacity,transform]',
            isLoaded
              ? 'opacity-100 scale-100 blur-0'
              : 'opacity-0 scale-[1.04] blur-sm',
            className
          )}
          referrerPolicy="no-referrer"
          {...props}
        />
      )}
    </div>
  );
}
